import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useId } from './react';
import { cx, useModal, usePresence, PassProps } from './hooks';
import { Icon, IconName } from './Icon';
import { Button } from './Button';
import { t } from '../i18n';

/* ---------------- Sheet (bottom sheet) ---------------- */
export interface SheetProps extends PassProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Hide the visible title but keep it for screen readers. */
  hideTitle?: boolean;
  children?: any;
  /** Sticky footer (primary action). */
  footer?: any;
  /** Dark plum hero styling. */
  tone?: 'default' | 'hero';
}

/** Bottom sheet: scrim, drag handle (drag down or tap scrim/Escape to close), focus trapped, focus restored. */
export function Sheet({ open, onClose, title, hideTitle, children, footer, tone = 'default', ...rest }: SheetProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { mounted, closing } = usePresence(open, 160);
  const [dy, setDy] = useState(0);
  const drag = useRef<{ y: number; t: number } | null>(null);
  const tid = useId();
  useModal(ref, open, onClose);

  useEffect(() => { if (!open) setDy(0); }, [open]);
  if (!mounted) return null;

  const down = (e: any) => { drag.current = { y: e.clientY, t: Date.now() }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const move = (e: any) => { if (drag.current) setDy(Math.max(0, e.clientY - drag.current.y)); };
  const up = () => {
    if (!drag.current) return;
    const v = dy / Math.max(1, Date.now() - drag.current.t);
    drag.current = null;
    if (dy > 90 || v > 0.6) onClose(); else setDy(0);
  };

  return (
    <div className={cx('pl-sheet-root', closing && 'is-closing')}>
      <div className="pl-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tid}
        tabIndex={-1}
        className={cx('pl-sheet', tone === 'hero' && 'pl-hero pl-sheet--hero', closing && 'pl-sheet--out')}
        style={dy ? { transform: `translateY(${dy}px)`, transition: 'none' } : undefined}
        {...rest}
      >
        <div className="pl-sheet__grab" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
          <span className="pl-sheet__handle" />
        </div>
        <div className="pl-sheet__head">
          <h2 id={tid} className={cx('pl-h3', hideTitle && 'pl-sr-only')}>{title}</h2>
          <button type="button" className="pl-iconbtn pl-iconbtn--tint pl-press" aria-label={t('common.a11y.closeSheet')} onClick={onClose}><Icon name="x" size={20} /></button>
        </div>
        <div className="pl-sheet__body">{children}</div>
        {footer ? <div className="pl-sheet__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ---------------- Dialog (rare: irreversible actions) ---------------- */
export interface DialogProps extends PassProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** State the consequence in one line. */
  children?: any;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  danger?: boolean;
  icon?: IconName;
}

export function Dialog({ open, onClose, title, children, confirmLabel, cancelLabel = t('common.ui.back'), onConfirm, danger, icon = danger ? 'alert' : 'info', ...rest }: DialogProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { mounted, closing } = usePresence(open, 120);
  const tid = useId();
  const did = useId();
  useModal(ref, open, onClose);
  if (!mounted) return null;
  return (
    <div className={cx('pl-dialog-root', closing && 'is-closing')}>
      <div className="pl-scrim" onClick={onClose} aria-hidden="true" />
      <div ref={ref} role="alertdialog" aria-modal="true" aria-labelledby={tid} aria-describedby={did} tabIndex={-1} className="pl-dialog" {...rest}>
        <span className={cx('pl-dialog__icon', danger && 'is-danger')}><Icon name={icon} size={26} /></span>
        <h2 id={tid} className="pl-h3">{title}</h2>
        <div id={did} className="pl-muted pl-dialog__body">{children}</div>
        <div className="pl-dialog__actions">
          <Button variant={danger ? 'danger' : 'primary'} size="lg" block onClick={onConfirm} data-testid="dialog-confirm">{confirmLabel}</Button>
          <Button variant="ghost" block data-autofocus data-testid="dialog-cancel" onClick={onClose}>{cancelLabel}</Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Toast ---------------- */
export interface ToastOptions {
  message: string;
  /** e.g. { label: 'ביטול', onAction } for Undo. */
  action?: { label: string; onAction: () => void };
  /** Optional second button next to the main action. */
  secondary?: { label: string; onAction: () => void };
  tone?: 'default' | 'success' | 'danger';
  /** ms, default 5000. */
  duration?: number;
  icon?: IconName;
}
interface ToastItem extends ToastOptions { id: number; closing?: boolean }
interface ToastApi { show: (t: ToastOptions) => number; dismiss: (id: number) => void; clear: () => void }

const ToastCtx = createContext<ToastApi>({ show: () => 0, dismiss: () => undefined, clear: () => undefined });
export const useToast = (): ToastApi => useContext(ToastCtx);

let seq = 1;

export function ToastProvider({ children }: { children?: any }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const dismiss = useCallback((id: number) => {
    setItems((xs: ToastItem[]) => xs.map((x) => (x.id === id ? { ...x, closing: true } : x)));
    setTimeout(() => setItems((xs: ToastItem[]) => xs.filter((x) => x.id !== id)), 160);
  }, []);
  const show = useCallback((t: ToastOptions) => {
    const id = seq++;
    // one toast at a time keeps the screen calm; a new one replaces the old
    setItems([{ ...t, id }]);
    return id;
  }, []);
  const clear = useCallback(() => setItems([]), []);
  const api = useMemo(() => ({ show, dismiss, clear }), [show, dismiss, clear]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="pl-toasts" role="region" aria-label={t('common.ui.toasts')}>
        <div aria-live="polite" aria-atomic="true" className="pl-toasts__live">
          {items.map((t: ToastItem) => <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />)}
        </div>
      </div>
    </ToastCtx.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const ms = item.duration ?? 5000;
  const timer = useRef<any>(null);
  // WCAG 2.2.1: the auto-dismiss timer pauses while the pointer rests on the toast or keyboard focus is inside it, and restarts afterwards.
  const left = useRef(ms);
  const startedAt = useRef(0);
  const arm = () => { clearTimeout(timer.current); startedAt.current = Date.now(); timer.current = setTimeout(onDismiss, Math.max(1500, left.current)); };
  const hold = () => { clearTimeout(timer.current); left.current = Math.max(1500, left.current - (Date.now() - startedAt.current)); };
  useEffect(() => {
    left.current = ms;
    arm();
    return () => clearTimeout(timer.current);
  }, [item.id]);
  const icon: IconName = item.icon ?? (item.tone === 'danger' ? 'alert' : 'check-circle');
  return (
    <div className={cx('pl-toast', item.tone && `pl-toast--${item.tone}`, item.closing && 'pl-toast--out')} role="status" data-testid="toast" data-tone={item.tone ?? 'default'} onMouseEnter={hold} onMouseLeave={arm} onFocus={hold} onBlur={arm}>
      <Icon name={icon} size={22} className="pl-toast__icon" />
      <span className="pl-toast__msg">{item.message}</span>
      {item.action ? (
        <button type="button" className="pl-toast__action pl-press" data-testid="toast-undo" onClick={() => { item.action!.onAction(); onDismiss(); }}>
          {item.action.label}
        </button>
      ) : null}
      {item.action && item.secondary ? (
        <button type="button" className="pl-toast__action pl-press" data-testid="toast-secondary" onClick={() => { item.secondary!.onAction(); onDismiss(); }}>
          {item.secondary.label}
        </button>
      ) : null}
      {item.action ? <span className="pl-toast__timer" style={{ animationDuration: `${ms}ms` }} aria-hidden="true" /> : null}
    </div>
  );
}
